import { useScopeHierarchy } from "./useScopeHierarchy";

/**
 * Which level of the organization tree the reader is looking at.
 *
 * Derived from the **narrowest active scope selection**, falling back to the
 * role. A selection is a deliberate narrowing and outranks who the reader is: a
 * platform admin who picked one company is reading that company's estate and
 * should get the company's shape, not the platform's. Reading the role first
 * would render the platform shape over one client's data.
 *
 * Shared rather than per-feature because two surfaces already needed it —
 * Desk's queue and the org dashboards — and each answering "what level am I"
 * with its own if-chain is how the two drift into disagreeing about whose
 * page this is. The backend derives the same answer from the resolved scope
 * header, so every level-aware endpoint and every level-aware page read one
 * rule.
 */
export type OrgLevel = "company" | "msp" | "distributor" | "platform";

/** The level directly beneath each one. `null` under a company: nothing is. */
export const CHILD_LEVEL: Record<OrgLevel, OrgLevel | null> = {
  platform: "distributor",
  distributor: "msp",
  msp: "company",
  company: null,
};

export const LEVEL_LABEL: Record<OrgLevel, { one: string; many: string }> = {
  platform: { one: "Platform", many: "Platform" },
  distributor: { one: "Distributor", many: "Distributors" },
  msp: { one: "MSP", many: "MSPs" },
  company: { one: "Company", many: "Companies" },
};

export function useOrgLevel(): OrgLevel {
  const {
    selectedCompanyId,
    selectedMspId,
    selectedDistributorId,
    isCompanyLevel,
    isMSPAdmin,
    isDistributor,
  } = useScopeHierarchy();

  if (selectedCompanyId || isCompanyLevel) return "company";
  if (selectedMspId || isMSPAdmin) return "msp";
  if (selectedDistributorId || isDistributor) return "distributor";
  return "platform";
}

/**
 * Which level the reader *is*, ignoring whatever the scope bar has selected.
 *
 * The distinction matters wherever the level decides what someone can **do**
 * rather than what they are **reading**. `useOrgLevel` lets a selection win,
 * which is right for a report: a platform admin who picked one company wants
 * that company's numbers in that company's shape.
 *
 * It is wrong for a workspace. Picking an MSP in the scope bar is a narrowing
 * of the reader's own view; it is not a change of identity, and it must never
 * be mistaken for one. Desk is the case: whose queue this is, who may be
 * assigned a ticket, what "escalated to me" means and which transitions exist
 * are all properties of the person, decided by their own anchor. A filter that
 * silently re-rendered Desk as the selected MSP's workspace would show a
 * distributor a queue that is not theirs and offer actions the server will
 * refuse.
 *
 * Read from the role in the token, so it is available on first paint. The
 * server answers the same question from the user's organization anchor, and
 * where the two could disagree the server is the authority.
 */
export function useIdentityLevel(): OrgLevel {
  const { isCompanyLevel, isMSPAdmin, isDistributor } = useScopeHierarchy();

  if (isCompanyLevel) return "company";
  if (isMSPAdmin) return "msp";
  if (isDistributor) return "distributor";
  return "platform";
}
